import { useCallback, useState } from "react";

import { useNavigate } from "react-router-dom";
import { Button, Box, Typography, Stack } from "@mui/material";

import HeaderBarcode from "../../shared/HeaderBarcode/HeaderBarcode";
import FooterBarcode from "../../shared/FooterBarcode/FooterBarcode";
import LiveCaptionOverlay from "../../UI/LiveCaptionOverlay/LiveCaptionOverlay";

import startGameBg from "../../../assets/images/map-bg-dstl-upd-11.webp";

import captions from "../../../assets/captions/WTUFO-Intro.en-GB.vtt";
import m1LoadingBg from "../../../assets/images/m1-loading-bg.png";
import m2LoadingBg from "../../../assets/images/mission-2-background.svg";
import m3LoadingBg from "../../../assets/images/loading-m3.webp";
import m4LoadingBg from "../../../assets/images/m4-loading-bg.png";

import sourceCodePro from "../../../assets/fonts/SourceCodePro-VariableFont_wght.ttf";
import orbitron from "../../../assets/fonts/Orbitron-VariableFont_wght.ttf";

import usePreloadAssets from "../../../hooks/usePreloadAssets";
import { useAudioPreloader } from "../../../hooks/useAudioPreload";

import "./StartPage.scss";
import GameLoader from "../../UI/GameLoader/GameLoader";

const SESSION_KEY = "introVideoWatched@session";
const LOCAL_KEY = "introVideoWatched@persist";

const PERSISTENCE_MODE = "local";

const hasWatchedIntro = () =>
  sessionStorage.getItem(SESSION_KEY) === "1" ||
  localStorage.getItem(LOCAL_KEY) === "1";

const markWatchedIntro = () => {
  if (PERSISTENCE_MODE === "local") {
    localStorage.setItem(LOCAL_KEY, "1");
  } else {
    sessionStorage.setItem(SESSION_KEY, "1");
  }
};

// Tutorial sprite keys for audio preloading
const ONBOARD_AUDIO_KEYS = [
  "after_code_generation",
  "code_generation",
  "welcome_board_intro",
  "level_rookie",
  "level_intermediate",
  "level_advanced",
  "objective_explanation",
  "map_intro",
  "hover_level",
  "setting_intro",
  "clue_board",
  "sign_out",
];

const Assets = [
  captions,
  m1LoadingBg,
  m2LoadingBg,
  m3LoadingBg,
  m4LoadingBg,
  sourceCodePro,
  orbitron,
];

const StartPage = () => {
  const navigate = useNavigate();
  const { loaded, progress } = usePreloadAssets(Assets);
  const { loading: audioLoading } = useAudioPreloader(ONBOARD_AUDIO_KEYS);
  const [showIntro, setShowIntro] = useState(false);

  //  First time: after intro -> generate code screen
  const finishIntro = useCallback(() => {
    markWatchedIntro();
    navigate("/generateInvestigationCode", { replace: true });
  }, [navigate]);

  //  Returning: go login
  //  First time: show intro overlay
  const handleStart = () => {
    setShowIntro(true);
  };

  const handleRules = () => {
    navigate("/rules");
  };

  return (
    <>
      {!loaded || audioLoading ? (
        <GameLoader loaded={loaded} progress={progress} />
      ) : (
        <>
          <Stack
            className="landing-container"
            style={{ backgroundImage: `url(${startGameBg})` }}
          >
            <Stack className="dotted-background">
              <Stack direction={"column"} className="container header">
                <HeaderBarcode />
              </Stack>

              <Stack className="landing-content" useFlexGap gap={4}>
                <Box className="title-container">
                  <Typography variant="h1" className="title">
                    WTUFO
                  </Typography>
                </Box>

                <Typography variant="body1" className="description">
                  Have you got what it takes to be an investigator? Play to test
                  your skills.
                </Typography>

                <Stack
                  className="btn-container"
                  direction={"row"}
                  useFlexGap
                  gap={3}
                >
                  <Button
                    variant="contained"
                    className="btn start-btn"
                    onClick={handleStart}
                  >
                    Start
                  </Button>
                  <Button
                    variant="contained"
                    className="btn rules-btn"
                    sx={{ backgroundColor: "#000", color: "#fff" }}
                    onClick={handleRules}
                  >
                    Rules
                  </Button>
                </Stack>
              </Stack>

              <Stack className="fc container footer">
                <FooterBarcode />
              </Stack>
            </Stack>
          </Stack>

          {showIntro && <LiveCaptionOverlay finishIntro={finishIntro} />}
        </>
      )}
    </>
  );
};

export default StartPage;
